import { param } from 'jquery';
import { Subject } from 'rxjs';
import { first as first_, tap, timestamp } from 'rxjs/operators';
import { Waitable } from './interfaces';

export class ContentManager {

    public index: number = 0;
    public resetIndex = new Subject<number>();;
    public messages: any[] = [];
    public revision = 0;
    public visibleRevision = 0;

    public isAudioOn = false;
    public synthActiveVoice = null;

    public inputs = new Subject<any>();
    public updated = new Subject<any>();
    public inputEnabled = false;
    public textArea = false;
    public inputKind = 'text';
    public inputMin;
    public inputMax;
    public inputStep;
    public inputSuggestions;
    public inputRequired;
    public placeholder = '';
    public buttonText = '';
    public validator = null;
    public fastScroll = false;
    public scrollLock = false;
    public fixme: () => void = null;
    public debug = false;

    public sendButtonText = 'Send';
    public inputPlaceholder = 'Type something...';
    public uploadFileText = 'Upload File...';
    public uploadedFileText = 'Uploaded Successfully';
    public notUploadedFileText = 'Failed to upload file';
    public fixmeMessage = 'Fix';
    public timeout = 1000;
    public backButton = true;
    public showAnimation: boolean = true;

    toQueue = [];

    constructor() { }

    clear() {
        this.messages = [];
        this.toQueue = [];
    }

    reportValue(value) {
        this.inputs.next(value);
    }

    reportUpdated(value) {
        if (this.showAnimation) {
            if (this.debug) {
                console.log('CONTENT UPDATED', this.timeout);
            }
            if (this.timeout) {
                window.setTimeout(() => {
                    this.updated.next(value);
                }, this.timeout);
            }
        }
    }

    add(kind, params) {
        const first = (
            this.messages.length === 0 ||
            kind !== this.messages[this.messages.length - 1].kind
        );
        const revision = this.revision;
        this.messages.push({ kind, params, first, revision });
        if (kind === "to") {
            this.playQuestionOnVoice(true);
        }
    }

    queue(kind, params) {
        this.toQueue.push({ kind, params });
        if (this.toQueue.length === 1) {
            this.typing();
        }
    }

    queueFunction(callable) {
        return new Promise((resolve) => {
            this.queue('function', { callable, resolve });
        });
    }

    typing() {
        if (this.debug) {
            console.log('TYPING, queue len=' + this.toQueue.length);
        }
        if (this.toQueue.length > 0) {
            const item = this.toQueue[0];
            if (this.debug) {
                console.log('item=', item);
            }
            if (item.kind === 'function') {
                if (this.debug) {
                    console.log('RUNNING FUNCTION', item);
                }
                this.toQueue.shift();
                const future = item.params.callable();
                future.then((result) => {
                    if (this.debug) {
                        console.log('FUNCTION RESOLVED to', result, item);
                    }
                    item.params.resolve(result);
                    this.typing();
                });
            } else {
                this.add('typing', null);
                const callback = () => {
                    this.toQueue.shift();
                    if (this.debug) {
                        console.log('handling item=', item);
                    }
                    if (item.kind === 'from' || item.kind === 'options') {
                        item.params["index"] = this.index;
                        this.index += 1;
                    }
                    this.replace(item.kind, item.params);
                    this.typing();
                };
                let timeout = this.timeout;
                if (this.toQueue.length > 0) {
                    let stepTimeout = this.toQueue[0].params.timeout;
                    if (stepTimeout || stepTimeout === 0) {
                        timeout = stepTimeout;
                    }
                }

                if (!this.showAnimation) {
                    timeout = 0;
                }
                if (timeout === 0) {
                    callback();
                } else {
                    window.setTimeout(() => {
                        callback();
                        this.reportUpdated(item);
                    }, timeout);
                }
            }
        } else {
            if (this.showAnimation) {
                window.setTimeout(async () => {
                    this.reportUpdated(null);
                }, this.timeout);
            }
        }
    }

    replace(kind, params) {
        const first = (this.messages.length < 2 || kind !== this.messages[this.messages.length - 2].kind);
        this.messages[this.messages.length - 1] = { kind, params, first };
        if (kind === "to") {
            this.playQuestionOnVoice(true);
        }
    }

    reportInput(value: string) {
        this.reportValue(value);
        this.reportUpdated(value);
        this.textArea = false;
        this.placeholder = '';
        this.validator = null;
    }

    queueFrom(message: string, timeout?) {
        this.queue('from', { message, fixme: this.fixme, fixmeMessage: this.fixmeMessage, timeout });
    }

    addTo(message: string, info?, timeout?) {
        this.queue('to', { message, info, timeout });
    }

    addOptions(message, options: any[], disableOptions: any[], selected?: any, multi?: boolean, emoji?: boolean, horizontal?: boolean, vertical?: boolean, className?: string) {
        if (message) {
            this.queue('to', { message });
        }
        this.queue('options', { options, disableOptions, selected, multi, emoji, horizontal, vertical, className, fixme: this.fixme, fixmeMessage: this.fixmeMessage });
    }

    addUploader(message, options?: any) {
        if (message) {
            this.queue('to', { message });
        }
        this.queue('uploader', options);
    }

    addCustomComponent(step, wait, timeout?) {
        return new Promise<void>((componentCreatedCallback) => {
            this.queue('component', {
                step,
                timeout,
                componentCreatedCallback: () => {
                    if (this.debug) {
                        console.log('CUSTOM COMPONENT CREATED', step);
                    }
                    return componentCreatedCallback();
                }
            });
        }).then(() => {
            if (wait) {
                return this.queueFunction(() => {
                    return (step.__instance as Waitable).wait();
                });
            } else {
                return;
            }
        });
    }

    setBackButton(backButton) {
        this.backButton = backButton;
    }

    resetToBackIndex(index) {
        this.resetIndex.next(index);
    }

    setSynthVoiceSetting(isAudioOn, voice, play) {
        this.isAudioOn = isAudioOn;
        this.synthActiveVoice = voice;
        if (play) {
            this.playQuestionOnVoice(false);
        }
    }

    playQuestTimer = null;
    playQuestionOnVoice(delay) {
        let timeout = 0;
        if (delay) {
            timeout = 0;
        }
        clearTimeout(this.playQuestTimer);
        this.playQuestTimer = setTimeout(() => {
            var msgIndex = null;
            var msg = null;
            for (var i = this.messages.length - 1; i >= 0; i--) {
                if (this.messages[i].kind === "to") {
                    msgIndex = i;
                    msg = this.messages[i];
                    break;
                }
            }
            if (msg) {
                this.playSynthVoice(msgIndex, msg.params.message);
            }
        }, timeout)

    }

    playSynthVoice(msgIndex, text) {
        if (this.isAudioOn && this.synthActiveVoice) {
            var msg = new SpeechSynthesisUtterance();
            msg.voice = this.synthActiveVoice;
            msg.lang = 'en-US';
            msg.rate = 1;
            msg.text = text;
            msg.onboundary = (event) => {
                this.onboundaryHandler(event, msgIndex, text);
            }
            msg.onend = (event) => {
                if (document.getElementById("say-text-" + msgIndex)) {
                    //document.getElementById("say-text-" + msgIndex).style.removeProperty("color");
                    document.getElementById("say-text-" + msgIndex).innerHTML = text;
                }
            }
            speechSynthesis.cancel();
            speechSynthesis.speak(msg);
        }
    }

    onboundaryHandler(event, msgIndex, text) {
        if (document.getElementById("say-text-" + msgIndex)) {
            var wordObj = this.getWordAtPos(text, event.charIndex);
            var words_array = text.split(/\s/);
            //document.getElementById("say-text-" + msgIndex).style.color = "#0067ff";

            //highlight word
            var wordIndex = 0;
            var html = "";
            for (var i = 0; i < words_array.length; i++) {
                if (wordIndex >= wordObj.left && wordIndex <= wordObj.right) {
                    html += `<span style="color:#0067ff;">${words_array[i]}</span> `;
                } else {
                    html += `${words_array[i]} `;
                }
                wordIndex += words_array[i].length + 1;
            }
            document.getElementById("say-text-" + msgIndex).innerHTML = html;
        }
    }

    getWordAtPos(str, pos) {
        // Perform type conversions.
        str = String(str);
        pos = Number(pos) >>> 0;

        // Search for the word's beginning and end.
        var left = str.slice(0, pos + 1).search(/\S+$/),
            right = str.slice(pos).search(/\s/);

        // The last word in the string is a special case.
        if (right < 0) {
            return { word: str.slice(left), left, right: str.length };
        }
        // Return the word, using the located bounds to extract it from the string.
        return { word: str.slice(left, right + pos), left, right: right + pos };
    }

    setShowAnimation(show) {
        this.showAnimation = show;
    }

    setTextArea() {
        this.textArea = true;
    }

    setInputKind(kind, required?, min?, max?, step?) {
        this.inputKind = kind || 'text';
        this.inputRequired = !!required,
            this.inputMin = min === undefined ? null : min;
        this.inputMax = max === undefined ? null : max;
        this.inputStep = step === undefined ? null : step;
    }

    setInputSuggestions(suggestions: string[]) {
        this.inputSuggestions = suggestions;
    }

    setPlaceholder(placeholder) {
        this.placeholder = placeholder;
    }

    setButtonText(buttonText) {
        this.buttonText = buttonText;
    }

    setValidator(validator) {
        this.validator = validator;
    }

    setFixme(fixme: () => void) {
        this.fixme = fixme;
    }

    setFastScroll(value: boolean) {
        this.fastScroll = value;
    }

    setScrollLock(value: boolean) {
        if (this.scrollLock !== value) {
            this.scrollLock = value;
            if (value) {
                this.revision += 1;
            } else {
                this.visibleRevision = this.revision;
                if (this.debug) {
                    console.log('SETTING VISIBLE REVISION', this.visibleRevision, 'LAST MESSAGE', this.messages[this.messages.length - 1]);
                }
            }
        }
    }

    async waitForInput(enableTextInput?) {
        enableTextInput = (enableTextInput !== false);
        let activeElement: HTMLElement | null = null;
        if (enableTextInput) {
            await this.queueFunction(async () => {
                if (this.debug) {
                    console.log('ENABLING INPUT');
                }
                activeElement = document.activeElement as HTMLElement;
                this.inputEnabled = true;
            });
        }
        return this.inputs.pipe(
            first_(),
            tap((value) => {
                if (this.debug) {
                    console.log('DISABLING INPUT, value=', value);
                }
                this.inputEnabled = false;
                if (this.debug) {
                    console.log('FOCUSING', activeElement);
                }
                activeElement?.focus();
            })
        ).toPromise();
    }

    setQueueTimeout(timeout) {
        let report = false;
        if (this.timeout === 0 && timeout !== 0) {
            report = true;
        }
        this.timeout = timeout;
        if (report) {
            this.reportUpdated(null);
        }
    }

}
